/**
 * Storage Utility Functions
 * Safe operations for JSON serialization and key management
 * @module @infra/storage
 */

/**
 * Safe JSON parse with error handling
 * @param json - JSON string to parse
 * @param defaultValue - Default value if parse fails
 * @returns Parsed value or default
 */
export function safeParse<T>(json: string, defaultValue: T): T {
  try {
    return JSON.parse(json) as T;
  } catch (error) {
    console.warn('JSON parse error:', error);
    return defaultValue;
  }
}

/**
 * Safe JSON stringify with error handling
 * @param value - Value to stringify
 * @returns JSON string or empty string if fails
 */
export function safeStringify<T>(value: T): string {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.error('JSON stringify error:', error);
    return '';
  }
}

/**
 * Generate storage key with prefix to avoid collisions
 * @param prefix - Namespace prefix (e.g., 'tma_sale_agents')
 * @param name - Key name (e.g., 'users')
 * @returns Prefixed key (e.g., 'tma_sale_agents_users')
 */
export function generateStorageKey(prefix: string, name: string): string {
  return `${prefix}_${name}`;
}

/**
 * Clear all storage keys matching a prefix
 * @param storage - Storage object (localStorage or sessionStorage)
 * @param prefix - Prefix to match (e.g., 'tma_sale_agents')
 */
export function clearByPrefix(storage: Storage, prefix: string): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(prefix)) {
      keys.push(key);
    }
  }
  keys.forEach((key) => storage.removeItem(key));
}

/**
 * Wipe localStorage wholesale except a set of preserved keys (e.g. the i18n language
 * choice). Reads preserved values as RAW strings — not via an adapter's `getItem`,
 * which JSON-parses — because localStorage mixes JSON-encoded values (this app's own
 * adapter-written keys) with plain-string values written directly via
 * `localStorage.setItem` (e.g. the i18n language key). JSON-parsing a plain string
 * like `"vi"` throws and would silently drop it. Raw get/set round-trips any value
 * untouched regardless of encoding.
 * @param preserveKeys - Exact key names to keep untouched across the wipe
 */
export function clearLocalStorageExcept(preserveKeys: string[]): void {
  try {
    const preserved = preserveKeys
      .map((key) => [key, localStorage.getItem(key)] as const)
      .filter((entry): entry is [string, string] => entry[1] !== null);
    localStorage.clear();
    preserved.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch (error) {
    console.error('Failed to clear localStorage except preserved keys', error);
  }
}
